import React, {FC} from 'react';
import ModalWrapper from "../ModalWrapper/ModalWrapper";

export type DiscardModalProps = {
    itemType?: string;
    isOpen: boolean;
    onClickKeepEditing: () => void,
    onClickDiscardChanges: () => void,
    headerText?: string,
    message?: string,
    keepEditingText?: string,
    discardChangesText?: string,
    isEditing: boolean
}

/**
 * DiscardModal component.
 * 
 * This component renders a modal dialog that prompts the user to confirm if they want to discard changes made to a form.
 * 
 * @component
 * @param itemType - The name of the form being edited. (deprecated)
 * @param isOpen - Flag indicating if the modal is open.
 * @param onClickKeepEditing - Callback function when the "Keep Editing" button is clicked.
 * @param onClickDiscardChanges - Callback function when the "Discard Changes" button is clicked.
 * @param isEditing - Flag indicating if the item is currently being edited or created for the first time.
 * @param headerText - The text to display in the modal header, defaults to "Discard changes?" (optional).
 * @param message - The message to display in the modal body, defaults to "Any data entered will not be saved." (optional).
 * @param keepEditingText - The text for the "Keep Editing" button, defaults to "Keep Editing" (optional).
 * @param discardChangesText - The text for the "Discard Changes" button, defaults to "Discard Changes" (optional).
 * @returns {JSX.Element} The rendered DiscardModal component.
 */
const DiscardModal: FC<DiscardModalProps> = ({ itemType, isOpen = false, onClickKeepEditing, onClickDiscardChanges, headerText="Discard changes?", message, keepEditingText="Keep Editing", discardChangesText="Discard Changes", isEditing = false }): JSX.Element => {
    if (itemType) {
        headerText = isEditing ? `Discard changes to this ${itemType}?` : `Discard this ${itemType}?`;
    }
    const messageTMP = message ?? (itemType
        ? `Any data entered for this ${itemType} will not be saved.`
        : "Any data entered will not be saved.");

    return (
        <ModalWrapper
            id="discard-changes-modal"
            isOpen={isOpen}
            headerText={headerText}
            buttons={[
                {
                    text: keepEditingText,
                    onClick: onClickKeepEditing,
                },
                {
                    text: discardChangesText,
                    onClick: onClickDiscardChanges,
                    destructive: true,
                    primary: true,
                }
            ]}
        >
            <div className="font-md">{messageTMP}</div>
        </ModalWrapper>
    );
}

export default DiscardModal;
